import { useEffect, useMemo, useState } from 'react';

type ViewportState = {
  /** Viewport width in pixels */
  width: number;
  /** Viewport height in pixels */
  height: number;
  /** Horizontal scroll position */
  scrollX: number;
  /** Vertical scroll position */
  scrollY: number;
};

type ViewportDerived = {
  /** Whether the page has been scrolled vertically */
  isScrolled: boolean;
  /** Aspect ratio (width / height) */
  aspectRatio: number;
  /** Whether viewport is in portrait orientation */
  isPortrait: boolean;
  /** Whether viewport is in landscape orientation */
  isLandscape: boolean;
  /** Tailwind sm breakpoint (640px) */
  isSm: boolean;
  /** Tailwind md breakpoint (768px) */
  isMd: boolean;
  /** Tailwind lg breakpoint (1024px) */
  isLg: boolean;
  /** Tailwind xl breakpoint (1280px) */
  isXl: boolean;
  /** Tailwind 2xl breakpoint (1536px) */
  is2xl: boolean;
};

export type Viewport = ViewportState & ViewportDerived;

const getViewportState = (): ViewportState => ({
  width: window.innerWidth,
  height: window.innerHeight,
  scrollX: window.scrollX,
  scrollY: window.scrollY,
});

/**
 * Hook that returns reactive viewport information.
 * Updates are throttled using requestAnimationFrame for performance.
 */
export default function useViewport(): Viewport {
  const [state, setState] = useState<ViewportState>(getViewportState);

  useEffect(() => {
    let rafId: number | null = null;

    const updateState = () => {
      rafId = null;
      setState(getViewportState());
    };

    const scheduleUpdate = () => {
      if (rafId === null) {
        rafId = requestAnimationFrame(updateState);
      }
    };

    window.addEventListener('resize', scheduleUpdate);
    window.addEventListener('scroll', scheduleUpdate, { passive: true });

    return () => {
      window.removeEventListener('resize', scheduleUpdate);
      window.removeEventListener('scroll', scheduleUpdate);
      if (rafId !== null) {
        cancelAnimationFrame(rafId);
      }
    };
  }, []);

  const derived = useMemo<ViewportDerived>(
    () => ({
      isScrolled: state.scrollY > 0,
      aspectRatio: state.width / state.height,
      isPortrait: state.height > state.width,
      isLandscape: state.width >= state.height,
      isSm: state.width >= 640,
      isMd: state.width >= 768,
      isLg: state.width >= 1024,
      isXl: state.width >= 1280,
      is2xl: state.width >= 1536,
    }),
    [state.width, state.height, state.scrollY],
  );

  return useMemo(() => ({ ...state, ...derived }), [state, derived]);
}
